import React, { useEffect, useState } from "react";
import { Package, Send, MapPin, AlertCircle, Clock } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";

const MaterialRequisition = () => {
  const { token, role } = useAdminAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form State
  const [form, setForm] = useState({ itemName: "", quantity: "", siteLocation: "", urgency: "MEDIUM" });

  const isEngineer = role === "EXECUTIVE"; 

  // Fetch Requests
  const fetchRequests = async () => {
    setLoading(true);
    try {
        // const res = await fetch("http://localhost:1001/api/admin/material/all", {
        const res = await fetch("https://luxuriyahomes.com/api/admin/material/all", {
            headers: { Authorization: `Bearer ${token}` }
        });
        if(res.ok) setRequests(await res.json());
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRequests(); }, []);

  // Submit Request
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if(!form.itemName || !form.siteLocation) return alert("Please fill all details");

    // await fetch("http://localhost:1001/api/admin/material/add", {
    await fetch("https://luxuriyahomes.com/api/admin/material/add", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(form)
    });
    
    setForm({ itemName: "", quantity: "", siteLocation: "", urgency: "MEDIUM" }); // Reset
    fetchRequests(); // Refresh List
    alert("Requisition Sent Successfully!");
  };

  return (
    <div className="p-6 md:p-10 text-white min-h-screen">
      <div className="flex items-center justify-between mb-8">
        <div>
            <h2 className="text-3xl font-light text-amber-500">Material Requisition</h2>
            <p className="text-zinc-500 text-sm mt-1">Manage site requirements & supplies</p>
        </div>
        {/* Admin Stats or Filter can go here */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* --- LEFT: REQUEST FORM (Only for Site Engineer) --- */}
        {isEngineer && (
            <div className="lg:col-span-1">
                <div className="bg-[#1c1c1c] p-6 rounded-2xl border border-zinc-800 sticky top-4 shadow-xl">
                    <h3 className="text-white font-bold mb-6 flex items-center gap-2 border-b border-zinc-800 pb-4">
                        <Package className="text-amber-500"/> New Request
                    </h3>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="text-[10px] uppercase text-zinc-500 font-bold tracking-wider">Material Name</label>
                            <input 
                                className="w-full bg-black border border-zinc-800 p-3 rounded text-white mt-1 focus:border-amber-500 outline-none"
                                placeholder="Ex: UltraTech Cement"
                                value={form.itemName}
                                onChange={e => setForm({...form, itemName: e.target.value})}
                            />
                        </div>

                        <div>
                            <label className="text-[10px] uppercase text-zinc-500 font-bold tracking-wider">Quantity / Unit</label>
                            <input 
                                className="w-full bg-black border border-zinc-800 p-3 rounded text-white mt-1 focus:border-amber-500 outline-none"
                                placeholder="Ex: 50 Bags"
                                value={form.quantity}
                                onChange={e => setForm({...form, quantity: e.target.value})}
                            />
                        </div>

                        <div>
                            <label className="text-[10px] uppercase text-zinc-500 font-bold tracking-wider">Site Location</label>
                            <input 
                                className="w-full bg-black border border-zinc-800 p-3 rounded text-white mt-1 focus:border-amber-500 outline-none"
                                placeholder="Ex: Dwarka Sector 21 Project"
                                value={form.siteLocation}
                                onChange={e => setForm({...form, siteLocation: e.target.value})}
                            />
                        </div>

                        <div>
                            <label className="text-[10px] uppercase text-zinc-500 font-bold tracking-wider">Urgency Level</label>
                            <select 
                                className="w-full bg-black border border-zinc-800 p-3 rounded text-white mt-1 focus:border-amber-500 outline-none"
                                value={form.urgency}
                                onChange={e => setForm({...form, urgency: e.target.value})}
                            >
                                <option value="LOW">Low - Can wait 2-3 days</option>
                                <option value="MEDIUM">Medium - Required by tomorrow</option>
                                <option value="HIGH">URGENT - Required Immediately</option>
                            </select>
                        </div>

                        <button className="w-full bg-amber-600 hover:bg-amber-500 text-black font-bold py-3 rounded mt-2 transition-all flex items-center justify-center gap-2">
                            <Send size={18}/> Send Request
                        </button>
                    </form>
                </div>
            </div>
        )}

        {/* --- RIGHT: REQUEST LIST (Visible to All) --- */}
        <div className={`${isEngineer ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
            <div className="bg-[#161616] rounded-2xl border border-zinc-800 overflow-hidden">
                <div className="p-4 bg-zinc-900/50 border-b border-zinc-800 flex justify-between items-center">
                    <h3 className="text-zinc-400 text-xs uppercase tracking-widest font-bold">Recent Requests</h3>
                    <span className="text-zinc-600 text-xs">{requests.length} Records</span>
                </div>

                <div className="divide-y divide-zinc-800/50">
                    {requests.length === 0 ? (
                        <p className="p-8 text-center text-zinc-500 italic">No material requests found.</p>
                    ) : (
                        requests.map((req: any) => (
                            <div key={req.id} className="p-5 hover:bg-zinc-800/20 transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                <div>
                                    <h4 className="text-lg font-bold text-white flex items-center gap-2">
                                        {req.itemName} 
                                        <span className="text-amber-500 bg-amber-900/10 px-2 py-0.5 rounded text-sm border border-amber-900/30">
                                            {req.quantity}
                                        </span>
                                    </h4>
                                    <div className="flex items-center gap-4 mt-2 text-zinc-400 text-xs">
                                        <span className="flex items-center gap-1"><MapPin size={12}/> {req.siteLocation}</span>
                                        <span className="flex items-center gap-1"><Clock size={12}/> {new Date(req.createdAt).toLocaleDateString()}</span>
                                        {!isEngineer && (
                                            <span className="text-zinc-500">• Req by: {req.requestedBy?.name}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${
                                        req.urgency === 'HIGH' ? 'bg-red-950 text-red-400 border-red-900' :
                                        req.urgency === 'MEDIUM' ? 'bg-orange-950 text-orange-400 border-orange-900' :
                                        'bg-zinc-800 text-zinc-400 border-zinc-700'
                                    }`}>
                                        {req.urgency} Urgency
                                    </span>
                                    
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                                        req.status === 'PENDING' ? 'bg-yellow-500/10 text-yellow-500' : 'bg-green-500/10 text-green-500'
                                    }`}>
                                        {req.status}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>

      </div>
    </div>
  );
};

export default MaterialRequisition;